import { forkJoin, from, map } from "rxjs";
import { BinanceUsdmRestClient } from "../../../market";
import { Deal, Order, OrderSide, OrderType, Position, PositionSide } from "src/app/core/model/trade";
import { parseFloatSafe } from "src/app/core/func";
import { FuturesAlgoOrderResponse, OrderResult, SymbolFromPaginatedRequestFromId } from "libs/binance-api/src";
import { DealsRequestParams } from "../model";

export class BinanceUsdmHistoryProvider {

  constructor(private rest: BinanceUsdmRestClient) {}

  requestPositions() {
    return from(this.rest.getPositionsV3()).pipe(
      map(data => data.map(row => {
        const position: Position = {
          createdTimestamp: row.updateTime,
          updateTimestamp: row.updateTime,
          market: 'binance',
          board: 'usdm',
          symbol: row.symbol,
          entryPrice: row.entryPrice,
          liquidationPrice: row.liquidationPrice,
          size: row.positionAmt,
          positionSide: row.positionSide == 'BOTH' ? PositionSide.both : row.positionSide == 'LONG' ? PositionSide.long : row.positionSide == 'SHORT' ? PositionSide.short : undefined,
          positionVolume: row.notional,
          unrealisedPnl: row.unRealizedProfit,
        }
        return position
      }).filter(position => parseFloatSafe(position.size) != 0))
    )
  }

  requestIncomes() {
    return from(this.rest.getIncomeHistory({limit: 1000}))
  }

  requestDeals(params: DealsRequestParams) {
    return from(this.rest.getAccountTrades(this.prepareDealsRequestParams(params))).pipe(
      map(data => {
        const deals: Deal[] = []
        data.forEach(row => {
          const deal: Deal = {
            timestamp: row.time,
            market: 'binance',
            board: 'usdm',
            symbol: row.symbol,
            price: row.price,
            quantity: row.qty,
            volume: parseFloatSafe(row.price) * parseFloatSafe(row.qty),
            fee: row.commission,
            feeAsset: row.commissionAsset,
            side: row.side == 'BUY' ? OrderSide.buy : OrderSide.sell,
            dealId: row.id,
            orderId: row.orderId,
            clientOrderId: '',
          }
          deals.push(deal)
        })
        return deals
      })
    )
  }

  requestOrders(params: DealsRequestParams) {
    return from(this.rest.getAllOrders(this.prepareDealsRequestParams(params))).pipe(
      map(data => data.map(row => this.fillOrder(row)))
    )
  }

  requestActiveOrders() {
    return forkJoin([this.rest.getAllOpenOrders(), this.rest.getOpenAlgoOrders()]).pipe(
      map(([data1, data2]) => {
        const orders = data1.map(row => this.fillOrder(row))
        const algoOrders = data2.map(row => this.fillAlgoOrder(row))
        return orders.concat(algoOrders)
      })
    )
  }

  private fillOrder(row: OrderResult) {
    const order: Order = {
      createdTimestamp: row.updateTime,
      updateTimestamp: row.updateTime,
      type: row.type == 'LIMIT' ? OrderType.limit : (row.type == 'MARKET' ? OrderType.market : OrderType.unknown),
      status: row.status,
      market: 'binance',
      board: 'usdm',
      symbol: row.symbol,
      price: row.price,
      quantity: row.origQty,
      volume: parseFloatSafe(row.price) * parseFloatSafe(row.origQty),
      remainingQuantity: parseFloatSafe(row.origQty) - parseFloatSafe(row.executedQty),
      remainingVolume: parseFloatSafe(row.price) * (parseFloatSafe(row.origQty) - parseFloatSafe(row.executedQty)),
      side: row.side == 'BUY' ? OrderSide.buy : OrderSide.sell,
      positionSide: row.positionSide == 'LONG' ? PositionSide.long : row.positionSide == 'SHORT' ? PositionSide.short : PositionSide.both,
      orderId: row.orderId,
      clientOrderId: row.clientOrderId,
      isStop: row.type.includes('STOP'),
      isClosePosition: row.reduceOnly,
    }
    if (order.isStop) {
      order.price = row.stopPrice
      order.volume = parseFloatSafe(order.price) * parseFloatSafe(order.quantity)
      order.remainingQuantity = order.quantity
      order.remainingVolume = order.volume
    }
    return order
  }

  private fillAlgoOrder(row: FuturesAlgoOrderResponse) {
    const order: Order = {
      createdTimestamp: row.updateTime,
      updateTimestamp: row.updateTime,
      type: OrderType.market,
      status: row.algoStatus,
      market: 'binance',
      board: 'usdm',
      symbol: row.symbol,
      price: parseFloatSafe(row.triggerPrice!),
      quantity: row.quantity,
      volume: parseFloatSafe(row.triggerPrice!) * parseFloatSafe(row.quantity),
      remainingQuantity: parseFloatSafe(row.quantity!),
      remainingVolume: parseFloatSafe(row.triggerPrice!) * parseFloatSafe(row.quantity!),
      side: row.side == 'BUY' ? OrderSide.buy : OrderSide.sell,
      positionSide: row.positionSide == 'LONG' ? PositionSide.long : row.positionSide == 'SHORT' ? PositionSide.short : PositionSide.both,
      orderId: row.algoId,
      clientOrderId: row.clientAlgoId,
      isStop: true,
      isClosePosition: row.reduceOnly,
    }
    return order
  }

  private prepareDealsRequestParams(params: DealsRequestParams) {
    if (!params.symbol) {
      throw new Error('DealsRequestParams.symbol required')
    }
    const params2: SymbolFromPaginatedRequestFromId = {
      symbol: params.symbol
    }
    params2.limit = params.limit ?? 1000
    return params2
  }

}
